import { ApolloProvider } from "@apollo/client";
import {
  AuthenticationResult,
  EventMessage,
  EventType,
  InteractionType,
} from "@azure/msal-browser";
import { useIsAuthenticated } from "@azure/msal-react";
import { captureException } from "@sentry/react";
import React, { useEffect, useState } from "react";
import { HashRouter } from "react-router-dom";
import { loginRequest } from "./auth/authConfig";
import {
  getTokenRedirect,
  msalInstance,
  setCustomerOrganizations,
  setLocalAccountId,
} from "./auth/authHandler";
import { LoadingElement } from "./components/Elements";

export const AuthLoader = () => {
  const [AppComponent, setAppComponent] = useState<React.JSX.Element>();
  const [redirectHandled, setRedirectHandled] = useState(false);
  const isAuthenticated = useIsAuthenticated();

  useEffect(() => {
    let initialized = false;

    // Wait for MSAL to settle any ongoing redirect interactions
    msalInstance
      .handleRedirectPromise()
      .catch((error) => {
        captureException(error);
      })
      .finally(() => {
        setRedirectHandled(true); // now it's safe to use MSAL methods
      });

    const handleAuthEvent = async (event: EventMessage) => {
      if (
        event.eventType === EventType.ACQUIRE_TOKEN_FAILURE &&
        event.interactionType === InteractionType.Silent
      ) {
        msalInstance.acquireTokenRedirect({ ...loginRequest });
      }
      if (
        event.eventType === EventType.LOGIN_SUCCESS ||
        event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
      ) {
        const payload = event.payload as AuthenticationResult;
        msalInstance.setActiveAccount(payload.account);

        sessionStorage.setItem(
          "localAccountId",
          payload.account.localAccountId,
        );
        sessionStorage.setItem("idToken", payload.idToken);
        setCustomerOrganizations();

        if (!initialized) {
          const { StoreProvider } = await import("./providers/StoreProvider");
          const { client } = await import("./services/ApolloClient");
          const { default: App } = await import("./App");

          setAppComponent(
            <ApolloProvider client={client}>
              <StoreProvider>
                <HashRouter>
                  <App />
                </HashRouter>
              </StoreProvider>
            </ApolloProvider>,
          );
          initialized = true;
        }
      }
    };

    const callbackId = msalInstance.addEventCallback(handleAuthEvent);

    return () => {
      if (callbackId) {
        msalInstance.removeEventCallback(callbackId);
      }
    };
  }, []);

  useEffect(() => {
    if (!redirectHandled) return;

    if (isAuthenticated && msalInstance.getAllAccounts().length) {
      if (!msalInstance.getActiveAccount()) {
        const account = msalInstance.getAllAccounts()[0];
        msalInstance.setActiveAccount(account);
        setLocalAccountId(account.localAccountId);
        getTokenRedirect();
      }
    }
  }, [redirectHandled, isAuthenticated]);

  return (
    AppComponent ?? <LoadingElement label="Preparing your secure session..." />
  );
};
